import Vue from 'vue';
import Component from 'vue-class-component';
import {AXIOS} from "@/lib/http-common";
import {WebServicesAuthType} from "@/components/web-services/model/WebServicesAuthType";
import {Oauth2ClientPropertiesDto} from "@/components/web-services/model/Oauth2ClientProperties";
import {BasicClientPropertiesDto} from "@/components/web-services/model/BasicClientProperties";
import UserActionResultSender from "@/components/application/UserActionResultSender";
import {WebServicesPropertiesDto} from "@/components/web-services/model/WebServicesProperties";
import {i18n} from "@/main";

@Component({
  computed: {
    WebServicesAuthType() {
      return WebServicesAuthType;
    }
  }
})
export default class WebServicesSettingsView extends Vue {
  public initializationFail: boolean = false;
  public showBasicPassword: boolean = false;
  public showOauth2Password: boolean = false;
  public loading: boolean = true;
  public databaseOauth2Configuration: Oauth2ClientPropertiesDto = this.getInitializedOauth2Configuration();
  public oauth2Configuration: Oauth2ClientPropertiesDto = this.getInitializedOauth2Configuration();
  public databaseBasicConfiguration: BasicClientPropertiesDto = this.getInitializedBasicConfiguration();
  public basicConfiguration: BasicClientPropertiesDto = this.getInitializedBasicConfiguration();
  public databaseWebServiceConfiguration: WebServicesPropertiesDto = this.getInitializedWebServicesConfiguration();
  public webServiceConfiguration: WebServicesPropertiesDto = this.getInitializedWebServicesConfiguration();
  public isGeneralConfigurationFormValid: any = null;
  public isOauth2ConfigurationFormValid: any = null;
  public isBasicConfigurationFormValid: any = null;

  public isRequired = [(value: boolean) => {
    if (value) {
      return true;
    }

    return i18n.tc('global.rules.required');
  }];

  public isURL = [(value: string) => {
    const expression = /^https?:\/\/.+(:[0-9]{4,5})?\/.*$/;

    const urlRegex = new RegExp(expression);

    return value.match(urlRegex) ? true : i18n.tc('global.rules.shouldBeAUrl');
  }];

  public mounted() {
    this.loadConfigurationsFromDatabase();
    this.resetValidationGeneralConfigurationForm();
  }

  public updateWebServiceConfiguration(): void {
    this.loading = true;
    AXIOS.put(`/configuration/webservice/general`, this.webServiceConfiguration)
      .then(() => {
        this.databaseWebServiceConfiguration = {...this.webServiceConfiguration};
        UserActionResultSender.showSuccessFeedback('webServices.success.generalConfigurationEdited');
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public updateOauth2Configuration(): void {
    this.loading = true;
    AXIOS.put(`/configuration/webservice/oauth2`, this.oauth2Configuration)
      .then(() => {
        this.databaseOauth2Configuration = {...this.oauth2Configuration};
        UserActionResultSender.showSuccessFeedback('webServices.success.oauth2ConfigurationEdited');
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public updateBasicConfiguration(): void {
    this.loading = true;
    AXIOS.put(`/configuration/webservice/basic`, this.basicConfiguration)
      .then(() => {
        this.databaseBasicConfiguration = {...this.basicConfiguration};
        UserActionResultSender.showSuccessFeedback('webServices.success.basicConfigurationEdited');
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public loadConfigurationsFromDatabase(): void {
    this.loading = true;
    AXIOS.get(`/configuration/webservice/general`)
      .then((generalMessage) => {
        this.databaseWebServiceConfiguration = {...generalMessage.data};
        this.webServiceConfiguration = {...generalMessage.data};

        AXIOS.get(`/configuration/webservice/oauth2`)
          .then((oauth2Message) => {
            this.databaseOauth2Configuration = {...oauth2Message.data};
            this.oauth2Configuration = {...oauth2Message.data};

            AXIOS.get(`/configuration/webservice/basic`)
              .then((basicMessage) => {
                this.databaseBasicConfiguration = {...basicMessage.data};
                this.basicConfiguration = {...basicMessage.data};
              })
              .catch(error => {
                this.initializationFail = true;
                UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
              })
              .finally(() => {
                this.loading = false;
              });
          })
          .catch(error => {
            this.loading = false;
            this.initializationFail = true;
            UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
          });
      })
      .catch(error => {
        this.loading = false;
        this.initializationFail = true;
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      });
  }

  public cancelGeneralConfigurationForm() {
    this.webServiceConfiguration = {...this.databaseWebServiceConfiguration};
    this.resetValidationGeneralConfigurationForm();
  }

  public resetValidationGeneralConfigurationForm() {
    (this.$refs.generalConfigurationForm as Vue & { resetValidation: () => boolean }).resetValidation();
  }

  public cancelOauth2ConfigurationForm() {
    this.oauth2Configuration = {...this.databaseOauth2Configuration};
    this.resetValidationOauth2ConfigurationForm();
  }

  public resetValidationOauth2ConfigurationForm() {
    (this.$refs.oauth2ConfigurationForm as Vue & { resetValidation: () => boolean }).resetValidation();
  }

  public cancelBasicConfigurationForm() {
    this.basicConfiguration = {...this.databaseBasicConfiguration};
    this.resetValidationBasicConfigurationForm();
  }

  public resetValidationBasicConfigurationForm() {
    (this.$refs.basicConfigurationForm as Vue & { resetValidation: () => boolean }).resetValidation();
  }

  public getInitializedOauth2Configuration(): Oauth2ClientPropertiesDto {
    return {
      clientId: '',
      clientSecret: '',
      authorizationGrantType: 'client_credentials',
      scopes: [],
      tokenUri: '',
      jwkSetUri: '',
    };
  }

  public getInitializedBasicConfiguration(): BasicClientPropertiesDto {
    return {
      username: '',
      password: ''
    };
  }

  public getInitializedWebServicesConfiguration(): WebServicesPropertiesDto {
    return {
      id: '',
      enabled: false,
      authType: WebServicesAuthType.NO_AUTH,
      createEndpoint: '',
      updateEndpoint: ''
    };
  }
}
